/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Chip,
  makeStyles,
} from "@material-ui/core";

import {
  AccountBalance,
  AttachMoney,
  Schedule,
  Description,
} from "@material-ui/icons";

import { useSelector } from "react-redux";
import ToastModal from "views/components/ToastModal";
import CircularProgresss from "views/components/CircularProgress/CircularProgress";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";

import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";

import {
  formatAmount,
  parseDateMDY,
  toCamelCase,
} from "utils/Utils";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    margin: "20px auto 40px",
  },

  summaryCard: {
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(0,0,0,.08)",
  },

  section: {
    padding: "25px 30px",

    [theme.breakpoints.down("sm")]: {
      padding: 20,
    },
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 15,
    },
  },

  titleContainer: {
    display: "flex",
    alignItems: "center",
  },

  iconWrapper: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "#ECF9F2",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10,

    "& svg": {
      color: "#19A65B",
      fontSize: 30,
    },
  },

  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#183A73",

    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
    },
  },

  link: {
    color: "#1A73E8",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
    textDecoration: "underline",
  },

  column: {
    paddingRight: 40,

    [theme.breakpoints.down("sm")]: {
      paddingRight: 0,
      marginBottom: 25,
    },
  },

  borderColumn: {
    borderLeft: "1px solid #ECECEC",
    paddingLeft: 40,

    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      paddingLeft: 0,
      marginTop: 20,
    },
  },

  label: {
    fontSize: 14,
    color: "#7C8798",
    marginBottom: 6,
  },

  value: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1A2F63",
    marginBottom: 10,

    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
    },
  },

  statusChip: {
    background: "#EAF8EF",
    color: "#169C55",
    fontWeight: 700,
    borderRadius: 18,
    padding: "4px 8px",
    width: "fit-content",
  },

  divider: {
    background: "#ECECEC",
  },

  emptyContainer: {
    width: "100%",
    textAlign: "center",
    marginTop: 120,
  },

  emptyTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#444",
  },

  emptySubtitle: {
    color: "#777",
    marginTop: 10,
  },
}));

const RepaymentLoanDetails = () => {
  const classes = useStyles();

  const navigate = useNavigate();
  const location = useLocation();

  const loanNumber = location.state?.loanNumber;

  const { authToken } = useSelector(
    (state) => state.login.authData
  );

  const [loanData, setLoanData] = useState({});
  const [callInProgress, setCallInProgress] =
    useState(false);

  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] =
    useState("");
  const [toastMessageType, setToastMessageType] =
    useState("");

  const toastUpdate = (
    type,
    message,
    showToast
  ) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };

  useEffect(() => {
    if (loanNumber) {
      fetchLoanSummary();
    }
  }, [loanNumber]);

  const fetchLoanSummary = async () => {
    setCallInProgress(true);

    const result = await apiHandler({
      url:
        endpoint.USER_LOAN_REPAYMENT_DETAILS_API +
        loanNumber,
      authToken,
    });

    setCallInProgress(false);

    if (result?.data?.error_code === 401) {
      toastUpdate(
        "Error",
        result?.data?.user_description,
        true
      );
    } else {
      setLoanData(result?.data);
    }
  };

  const handlePayNow = () => {
    navigate("/PayNow", {
      state: {
        loanNumber,
      },
    });
  };

  const handleRepaymentSchedule = () => {
    navigate("/EmiSchedule", {
      state: {
        loanNumber,
      },
    });
  };

  const handleStatement = () => {
    navigate("/StatementOfAccount", {
      state: {
        loanNumber,
      },
    });
  };
    return (
    <>
      <Grid item xs={12}>
        <SubHeader
          label="Loan Summary"
          button1="Pay Now"
          button1click={handlePayNow}
          cancelButtonUrl="/Repayment"
        />
      </Grid>

      {!loanData?.loanAccountNumber ? (
        <div className={classes.emptyContainer}>
          <Typography className={classes.emptyTitle}>
            Your loan is under progress...
          </Typography>

          <Typography className={classes.emptySubtitle}>
            Please wait some time.
          </Typography>
        </div>
      ) : (
        <Container className={classes.container}>
          <Paper className={classes.summaryCard}>

            {/* ===========================
                  LOAN DETAILS
            ============================ */}

            <div className={classes.section}>

              <div className={classes.sectionHeader}>

                <div className={classes.titleContainer}>
                  <div className={classes.iconWrapper}>
                    <AccountBalance />
                  </div>

                  <Typography className={classes.sectionTitle}>
                    Loan Details
                  </Typography>
                </div>

                <Typography
                  className={classes.link}
                  onClick={handleStatement}
                >
                  <Description
                    style={{
                      fontSize: 18,
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />
                  Statement of Account
                </Typography>
              </div>

              <Grid container>

                {/* COLUMN 1 */}

                <Grid item md={4} xs={12} className={classes.column}>

                  <Typography className={classes.label}>
                    Loan Account Number
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.loanAccountNumber}
                  </Typography>

                  <Typography className={classes.label}>
                    Status
                  </Typography>

                  <Chip
                    className={classes.statusChip}
                    label={toCamelCase(loanData.status)}
                  />

                </Grid>

                {/* COLUMN 2 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.borderColumn}
                >

                  <Typography className={classes.label}>
                    Customer Id
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.customerId}
                  </Typography>

                  <Typography className={classes.label}>
                    Disbursal Date
                  </Typography>

                  <Typography className={classes.value}>
                    {parseDateMDY(
                      loanData.disbursalDate
                    )}
                  </Typography>

                </Grid>

                {/* COLUMN 3 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.borderColumn}
                >

                  <Typography className={classes.label}>
                    Product Type
                  </Typography>

                  <Typography className={classes.value}>
                    {toCamelCase(
                      loanData.productType
                    )}
                  </Typography>

                </Grid>

              </Grid>

            </div>

            <Divider className={classes.divider} />

            {/* ===========================
                 FINANCIAL DETAILS
               =========================== */}
                           <div className={classes.section}>

              <div className={classes.sectionHeader}>

                <div className={classes.titleContainer}>
                  <div className={classes.iconWrapper}>
                    <AttachMoney />
                  </div>

                  <Typography className={classes.sectionTitle}>
                    Financial Details
                  </Typography>
                </div>

              </div>

              <Grid container>

                {/* COLUMN 1 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.column}
                >

                  <Typography className={classes.label}>
                    Loan Amount
                  </Typography>

                  <Typography className={classes.value}>
                    {formatAmount(
                      loanData.loanAmount
                    )}
                  </Typography>

                  <Typography className={classes.label}>
                    Rate Type
                  </Typography>

                  <Typography className={classes.value}>
                    {toCamelCase(
                      loanData.rateType
                    )}
                  </Typography>

                </Grid>

                {/* COLUMN 2 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.borderColumn}
                >

                  <Typography className={classes.label}>
                    Balance Amount
                  </Typography>

                  <Typography className={classes.value}>
                    {formatAmount(
                      loanData.balanceAmount
                    )}
                  </Typography>

                  <Typography className={classes.label}>
                    Currency
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.currency}
                  </Typography>

                </Grid>

                {/* COLUMN 3 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.borderColumn}
                >

                  <Typography className={classes.label}>
                    Interest Rate
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.rateOfInterest}%
                  </Typography>

                  <Typography className={classes.label}>
                    Total Over Due
                  </Typography>

                  <Typography className={classes.value}>
                    {formatAmount(
                      loanData.totalOverDue
                    )}
                  </Typography>

                </Grid>

              </Grid>

            </div>

            <Divider className={classes.divider} />

            {/* ===========================
                  INSTALLMENTS
            =========================== */}
                        <div className={classes.section}>

              <div className={classes.sectionHeader}>
                <div className={classes.titleContainer}>
                  <div className={classes.iconWrapper}>
                    <Schedule />
                  </div>

                  <Typography className={classes.sectionTitle}>
                    Installments
                  </Typography>
                </div>
              </div>

              <Grid container>

                {/* COLUMN 1 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.column}
                >
                  <Typography className={classes.label}>
                    Tenure (Months)
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.tenureMonths}
                  </Typography>

                  <Typography className={classes.label}>
                    Installments Unpaid
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.installmentsUnpaid}
                  </Typography>
                </Grid>

                {/* COLUMN 2 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.borderColumn}
                >
                  <Typography className={classes.label}>
                    Installment Frequency
                  </Typography>

                  <Typography className={classes.value}>
                    {toCamelCase(
                      loanData.installmentFrequency
                    )}
                  </Typography>

                  <Typography className={classes.label}>
                    Next Installment Date
                  </Typography>

                  <Typography className={classes.value}>
                    {parseDateMDY(
                      loanData.nextEmiDate
                    )}
                  </Typography>
                </Grid>

                {/* COLUMN 3 */}

                <Grid
                  item
                  md={4}
                  xs={12}
                  className={classes.borderColumn}
                >
                  <Typography className={classes.label}>
                    Balance Installments
                  </Typography>

                  <Typography className={classes.value}>
                    {loanData.balanceInstallments}
                  </Typography>

                  <Typography className={classes.label}>
                    Next Installment Amount
                  </Typography>

                  <Typography className={classes.value}>
                    {formatAmount(
                      loanData.nextDueInstallment
                    )}
                  </Typography>
                </Grid>

              </Grid>

              <Grid
                container
                style={{
                  marginTop: 10,
                }}
              >
                <Grid item xs={12}>
                  <Typography
                    className={classes.link}
                    onClick={
                      handleRepaymentSchedule
                    }
                  >
                    View Repayment Schedule →
                  </Typography>
                </Grid>
              </Grid>

            </div>

          </Paper>
        </Container>
      )}

      {callInProgress && (
        <CircularProgresss
          callInProgress={
            callInProgress
          }
        />
      )}

      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={
            toastMessageType
          }
          toastUpdate={toastUpdate}
        />
      )}
    </>
  );
};

export default RepaymentLoanDetails;